import { createFileRoute, Outlet, redirect } from "@tanstack/react-router";
import { SettingsProviderOrganization } from "@/contexts/ContextOrgSettings";
import {
	db,
	getIssueTemplates,
	getLabels,
	getOrganization,
	getOrgPermissions,
	getReleases,
	getTasksByOrganizationId,
	schema,
} from "@repo/database";
import { createServerFn } from "@tanstack/react-start";
import { eq } from "drizzle-orm";
import { PermissionError } from "@repo/util";
import { requireAccount } from "@/getAccess";
import { seo } from "@/seo";

export const getAdminOrganizationSettings = createServerFn({ method: "GET" })
	.validator((data: { orgId: string }) => data)
	.handler(async ({ data }) => {
		const { orgId } = data;
		// Identity comes from the session, never from client input (server fns are publicly callable).
		const account = await requireAccount();
		try {
			if (!orgId) {
				throw redirect({ to: "/" });
			}
			const organization = await getOrganization(orgId, account.id, { includeUnseated: true });
			if (!organization) {
				throw redirect({ to: "/" });
			}
			const labels = await getLabels(organization.id);
			const tasks = await getTasksByOrganizationId(organization.id);
			const views = await db
				.select()
				.from(schema.savedView)
				.where(eq(schema.savedView.organizationId, organization.id));
			const categories = await db.query.category.findMany({
				where: (category) => eq(category.organizationId, organization.id),
			});
			const issueTemplates = await getIssueTemplates(organization.id);
			const releases = await getReleases(organization.id);
			const permissions = await getOrgPermissions(account.id, organization.id);
			return { organization, labels, views, categories, tasks, issueTemplates, releases, permissions };
		} catch (error) {
			console.log("🚀 ~ error:", error);
			// If it's already a redirect, re-throw it
			if (error && typeof error === "object" && "redirect" in error) {
				throw error;
			}
			throw redirect({ to: "/" });
		}
	});
export const getUserOrgPermissions = createServerFn({ method: "GET" })
	.validator((data: { orgId: string }) => data)
	.handler(async ({ data }) => {
		try {
			const account = await requireAccount();
			const permissions = await getOrgPermissions(account.id, data.orgId);
			return {
				account,
				orgId: data.orgId,
				permissions,
			};
		} catch (error) {
			console.error("Error fetching org permissions:", error);
			if (error && typeof error === "object" && "redirect" in error) {
				throw error;
			}
			throw redirect({ to: "/auth/login" });
		}
	});
export const Route = createFileRoute("/(admin)/settings/org/$orgId")({
	beforeLoad: async ({ params, context, location }) => {
		const { account } = context;

		// ❌ Skip non-app / internal routes
		if (location.external || location.pathname.startsWith("/.well-known")) {
			return;
		}

		const searchParams = new URLSearchParams(location.search);
		const currentUrl = `${location.pathname}${searchParams.toString() ? `?${searchParams.toString()}` : ""}`;

		if (!account) {
			throw redirect({
				to: "/auth/login",
				headers: {
					"Set-Cookie": `post_login_redirect=${encodeURIComponent(currentUrl)}; Path=/; HttpOnly; SameSite=Lax`,
				},
			});
		}

		const { permissions } = await getUserOrgPermissions({
			data: {
				orgId: params.orgId,
			},
		});

		return { permissions };
	},
	loader: async ({ params, context }) => {
		if (!context.account) {
			throw redirect({ to: "/auth/login" });
		}
		const org = await getAdminOrganizationSettings({
			data: {
				orgId: params.orgId,
			},
		});
		if (!org.organization.members.find((e) => e.userId === context?.account?.id)?.seatAssigned) {
			throw new PermissionError();
		}
		return org;
	},
	head: ({ loaderData }) => ({
		meta: seo({
			title: loaderData?.organization?.name ? `${loaderData.organization.name} · Settings` : "Settings",
		}),
	}),
	component: RouteComponent,
});

function RouteComponent() {
	const { organization, labels, views, categories, tasks, issueTemplates, releases, permissions } =
		Route.useLoaderData();
	return (
		<SettingsProviderOrganization
			organization={organization}
			labels={labels}
			views={views}
			categories={categories}
			tasks={tasks}
			issueTemplates={issueTemplates}
			releases={releases}
			permissions={permissions}
		>
			<Outlet />
		</SettingsProviderOrganization>
	);
}
